import React, { useEffect, useState } from "react";

import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Alert,
  Empty,
  Spin,
  message,
} from "antd";

import {
  IdcardOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import { pdf } from "@react-pdf/renderer";
import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";
import StudentIDCardPDF from "./StudentIDCardPDF";

const { Title, Text } = Typography;
const { Option } = Select;

const StudentsIDCard = () => {
  const { selectedYear } = useAcademicYear();

  const [gradeClasses, setGradeClasses] = useState([]);
  const [selectedGradeClass, setSelectedGradeClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!selectedYear?.id) {
      setStudents([]);
      setSelectedGradeClass(null);
      return;
    }

    fetchGradeClasses();
  }, [selectedYear?.id]);

  const fetchGradeClasses = async () => {
    try {
      setLoadingClasses(true);
      setError("");

      const response = await api.get("api/users/grade-classes/");
      const data = response.data;

      if (Array.isArray(data)) {
        setGradeClasses(data);
        return;
      }

      if (Array.isArray(data?.results)) {
        setGradeClasses(data.results);
        return;
      }

      setGradeClasses([]);
    } catch (err) {
      console.error("Failed to fetch grade classes:", err);
      setError("Unable to load grade classes.");
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchStudents = async (gradeClassId) => {
    if (!selectedYear?.id || !gradeClassId) {
      setStudents([]);
      return;
    }

    try {
      setLoadingStudents(true);
      setError("");

      const response = await api.get("/student-identification/", {
        params: {
          academic_year: selectedYear.id,
          grade_class: gradeClassId,
        },
      });

      const data = response.data;
      const results = Array.isArray(data) ? data : data?.results || [];

      setStudents(results);

      if (!results.length) {
        messageApi.info(
          "No students were found in this class for the selected academic year."
        );
      }
    } catch (err) {
      console.error("Failed to fetch students:", err);
      setStudents([]);

      setError(
        err?.response?.data?.detail ||
          "Unable to load students for this class."
      );
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleClassChange = (value) => {
    setSelectedGradeClass(value);
    setStudents([]);
    fetchStudents(value);
  };

  const generateIDCards = async () => {
    if (!selectedYear?.id) {
      messageApi.warning("No active academic year is selected.");
      return;
    }

    if (!selectedGradeClass) {
      messageApi.warning("Please select a grade class.");
      return;
    }

    if (!students.length) {
      messageApi.warning("There are no students available for this class.");
      return;
    }

    try {
      setGenerating(true);

      const selectedClass = gradeClasses.find(
        (gradeClass) =>
          String(gradeClass.id) === String(selectedGradeClass)
      );

      const blob = await pdf(
        <StudentIDCardPDF
          students={students}
          academicYear={selectedYear}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;

      const className =
        selectedClass?.name ||
        selectedClass?.class_name ||
        "Grade-Class";

      const safeClassName = className
        .replace(/[^a-z0-9]/gi, "-")
        .toLowerCase();

      link.download = `student-id-cards-${safeClassName}-${selectedYear.name}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);

      messageApi.success(
        `${students.length} student ID card${
          students.length > 1 ? "s" : ""
        } generated successfully.`
      );
    } catch (err) {
      console.error("Failed to generate ID cards:", err);
      messageApi.error("Failed to generate student ID cards.");
    } finally {
      setGenerating(false);
    }
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_, record, index) => index + 1,
    },
    {
      title: "Student",
      dataIndex: "student_name",
      key: "student_name",
      render: (value) => value || "Unnamed Student",
    },
    {
      title: "Student ID",
      dataIndex: "card_number",
      key: "card_number",
      render: (value) => (
        <Tag color="maroon">{value || "N/A"}</Tag>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: "Division",
      dataIndex: "division",
      key: "division",
    },
    {
      title: "Academic Year",
      dataIndex: "academic_year_name",
      key: "academic_year_name",
      render: (value) => (
        <Tag color="blue">
          {value || selectedYear?.name}
        </Tag>
      ),
    },
  ];

  return (
    <>
      {contextHolder}

      <Card
        bordered={false}
        style={{
          borderRadius: 12,
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{
            width: "100%",
          }}
        >
          <div>
            <Title
              level={3}
              style={{
                marginBottom: 4,
              }}
            >
              <IdcardOutlined /> Student Identification Cards
            </Title>

            <Text type="secondary">
              Generate professional student identification cards for the active
              academic year.
            </Text>
          </div>

          {!selectedYear ? (
            <Alert
              type="warning"
              showIcon
              message="No active academic year selected"
              description="Please activate an academic year before generating student identification cards."
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message={`Active Academic Year: ${selectedYear.name}`}
              description="Only students enrolled in the active academic year will be included."
            />
          )}

          <Space
            wrap
            style={{
              width: "100%",
            }}
          >
            <Select
              placeholder="Select Grade Class"
              value={selectedGradeClass}
              onChange={handleClassChange}
              loading={loadingClasses}
              disabled={!selectedYear}
              style={{
                minWidth: 280,
              }}
              showSearch
              optionFilterProp="children"
            >
              {gradeClasses.map((gradeClass) => (
                <Option
                  key={gradeClass.id}
                  value={gradeClass.id}
                >
                  {gradeClass.name ||
                    gradeClass.class_name ||
                    gradeClass.grade_name ||
                    `Class ${gradeClass.id}`}
                </Option>
              ))}
            </Select>

            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                if (selectedGradeClass) {
                  fetchStudents(selectedGradeClass);
                }
              }}
              disabled={!selectedGradeClass || loadingStudents}
            >
              Refresh
            </Button>

<Button
  type="primary"
  icon={<FilePdfOutlined />}
  loading={generating}
  disabled={
    !selectedGradeClass ||
    !students.length ||
    loadingStudents
  }
  onClick={generateIDCards}
  style={{
    backgroundColor: "#000000",
    border: "2px solid #f39c12",
    color: "#ffffff",
    fontWeight: 700,
    transition: "all 0.3s ease",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.backgroundColor = "#ffcc00";
    e.currentTarget.style.borderColor = "#ffcc00";
    e.currentTarget.style.color = "#000000";
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow =
      "0 6px 18px rgba(255, 204, 0, 0.45)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.backgroundColor = "#000000";
    e.currentTarget.style.borderColor = "#f39c12";
    e.currentTarget.style.color = "#ffffff";
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "none";
  }}
>
  Generate ID Cards
</Button>
          </Space>

          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
            />
          )}

          {loadingStudents ? (
            <div
              style={{
                textAlign: "center",
                padding: 40,
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <Table
              rowKey={(record) => record.id}
              columns={columns}
              dataSource={students}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
              }}
              locale={{
                emptyText: (
                  <Empty description="Select a grade class to view students" />
                ),
              }}
              scroll={{
                x: true,
              }}
            />
          )}
        </Space>
      </Card>
    </>
  );
};

export default StudentsIDCard;